import React, { useEffect, useState } from "react";
import { UseAppDispatch } from "redux/store";
import { useSelector } from "react-redux";
import {
  goToTraveler,
  travelerInformationSelector,
  ITraveler,
} from "redux/travelerInformation";
import { itinerarySelector } from "redux/selectedItinerary";
import TravelerInformationSummary from "./TravelerInformationSummary";
import Traveler from "./Traveler";
import { useTranslations } from "hooks/useTranslations";
import { Box, Button, SxProps } from "@mui/material";
import {
  MediaQuery,
  pushToDataLayer,
  CustomAnimation,
} from "@thekilroygroup/shared-ui";
import { useForm } from "react-hook-form";

interface ITravelerInformationProps {
  validateHandler: (valid: boolean) => void;
  handleProceed: () => void;
  initialValidState: boolean;
}

export default function TravelerInformation(props: ITravelerInformationProps) {
  const { validateHandler, handleProceed } = props;
  const { translations } = useTranslations();
  const dispatch = UseAppDispatch();
  const travelerSlice = useSelector(travelerInformationSelector);
  const travelers = travelerSlice.travelers;
  const itinerary = useSelector(itinerarySelector);
  const [validList, setValidList] = useState(travelers.map(() => false));
  const [allIsValid, setAllIsValid] = useState(false);
  const [shouldTrigger, setShouldTrigger] = useState(false);

  const buttonWrapperSx: SxProps = {
    px: { xs: 2, md: 0 },
    pb: { xs: 4, md: 0 },
    mt: 4,
    display: { md: "flex" },
    justifyContent: { md: "flex-end" },
  };

  const handleNextTravelerClick = (): void => {
    pushToDataLayer(
      "Step 2 - Traveller information",
      "Click next traveller - mobile",
    );
    dispatch(goToTraveler(travelerSlice.currentTraveler + 1));
    window.scrollTo(0, 0);
  };

  const clickNextButton = () => {
    if (allIsValid) {
      pushToDataLayer("Step 2 - Traveller information", "Click next step");
      handleProceed();
    } else {
      setShouldTrigger(true);
    }
  };
  useEffect(() => {
    if (shouldTrigger) {
      setShouldTrigger(false);
    }
  }, [shouldTrigger]);

  const onValidityChange = (index: number, isValid: boolean) => {
    validList[index] = isValid;
    setValidList([...validList]);
    const allIsValid = validList.every((valid: boolean) => valid);
    setAllIsValid(allIsValid);
    validateHandler(allIsValid);
  };

  const { handleSubmit } = useForm();
  return (
    <>
      <MediaQuery min="md">
        <Box component="form" onSubmit={handleSubmit(clickNextButton)}>
          {travelers.map((traveler: ITraveler, index: number) => (
            <Traveler
              traveler={traveler}
              index={index}
              key={index}
              onValidityChange={(isValid: boolean) =>
                onValidityChange(index, isValid)
              }
              shouldTriggerValidation={shouldTrigger}
              youthStudentCardPrice={
                itinerary.youthStudentCardPrice || undefined
              }
            />
          ))}
          <Box sx={buttonWrapperSx}>
            <Box onClick={() => clickNextButton()}>
              <Button
                color="primary"
                size="large"
                variant="outlined"
                disabled={!allIsValid}
                disableElevation
                type="submit"
              >
                {translations.navigation.nextStep}
              </Button>
            </Box>
          </Box>
        </Box>
      </MediaQuery>
      <MediaQuery max="md">
        {travelers.map(
          (traveler: ITraveler, index: number) =>
            travelerSlice.currentTraveler === index && (
              <CustomAnimation
                key={`traveller-tablet-${index}`}
                in={true}
                type={"fade"}
                timeout={250}
              >
                <Traveler
                  traveler={traveler}
                  index={index}
                  onValidityChange={(isValid: boolean) =>
                    onValidityChange(index, isValid)
                  }
                  youthStudentCardPrice={
                    itinerary.youthStudentCardPrice || undefined
                  }
                />
              </CustomAnimation>
            ),
        )}
        {travelerSlice.currentTraveler === travelers.length && (
          <CustomAnimation in={true} type={"fade"} timeout={250}>
            <Box>
              <TravelerInformationSummary />
            </Box>
          </CustomAnimation>
        )}
        {travelerSlice.currentTraveler < travelers.length && (
          <Box sx={buttonWrapperSx}>
            <Button
              color="primary"
              size="xLarge"
              variant="contained"
              fullWidth={true}
              disableElevation
              disabled={!validList[travelerSlice.currentTraveler]}
              onClick={handleNextTravelerClick}
            >
              {translations.navigation.nextStep}
            </Button>
          </Box>
        )}
        {travelerSlice.currentTraveler === travelers.length && (
          <Box sx={buttonWrapperSx}>
            <Button
              color="primary"
              size="xLarge"
              variant="contained"
              disabled={!allIsValid}
              fullWidth={true}
              disableElevation
              onClick={() => clickNextButton()}
            >
              {translations.navigation.nextStep}
            </Button>
          </Box>
        )}
      </MediaQuery>
    </>
  );
}
